import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { AgrNumberDirective } from '@next/core-lib/form';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Subject, Subscription } from 'rxjs';

import { debounceTime } from 'rxjs/operators';

import { DosatorConversions } from '../../models/settings.model';

@Component({
  selector: 'app-dosator-setting-product-item-table',
  templateUrl: './dosator-setting-product-item-table.html',
  styleUrls: ['./dosator-setting-product-item-table.scss'],
  imports: [
    MatTable,
    ReactiveFormsModule,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    MatInput,
    AgrNumberDirective,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatButton,
    MatIcon,
    TranslatePipe,
  ],
})
export class DosatorSettingProductTableComponent implements OnDestroy, AfterContentChecked {
  @Input() formGroup: FormGroup;
  @Input() conversions: DosatorConversions[];

  @Output() updateSummaryTable = new EventEmitter<void>();

  changeNumberOfLinesClickStream$ = new Subject<string>();

  datasource: MatTableDataSource<DosatorConversions>;

  columns = ['liter', 'kilogram', 'kilogramPerLiter'];
  displayedColumns: Array<string> = this.columns;

  changedTableField: { column: string; value: number };
  subscriptions: Subscription[][] = [];

  constructor(private cdref: ChangeDetectorRef) {
    this.changeNumberOfLinesClickStream$.pipe(debounceTime(200)).subscribe((operation) => {
      this.recalculateNumberOfLines(operation);
    });
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  calculateTableRows(conversions: DosatorConversions[]): DosatorConversions[] {
    const rows = new Array<DosatorConversions>();
    if (conversions) {
      for (let i = 0; i < conversions.length; i++) {
        const currentLine = <DosatorConversions>conversions[i];

        rows.push(<DosatorConversions>{
          liter: currentLine.liter,
          kilogram: currentLine.kilogram,
          kilogramPerLiter: currentLine.kilogramPerLiter,
        });
      }
    }
    return rows;
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscriptionList) =>
        subscriptionList.forEach((s) => {
          if (s) {
            s.unsubscribe();
          }
        }),
      );
    }
  }

  getDataSource(source: DosatorConversions[]): MatTableDataSource<DosatorConversions> {
    this.datasource = new MatTableDataSource(source);
    return this.datasource;
  }

  changeNumberOfLines(operation: string) {
    this.changeNumberOfLinesClickStream$.next(operation);
  }
  recalculateNumberOfLines(operation: string) {
    const getNewTableRow = (): DosatorConversions => {
      const newTableRow = <DosatorConversions>{
        liter: null,
        kilogram: null,
        kilogramPerLiter: null,
      };
      return newTableRow;
    };

    const addControlToFormArray = (index: number, row: DosatorConversions) => {
      const formArray = this.formGroup.controls.lines as FormArray;

      formArray.insert(index, this.createConversionFormControl(row));
      formArray.controls[index].patchValue({
        liter: null,
        kilogram: null,
        kilogramPerLiter: null,
      });
    };

    switch (operation) {
      case 'add-bottom': {
        const index = this.conversions.length;
        const bottomRow = getNewTableRow();
        this.conversions = [...this.conversions, bottomRow];
        addControlToFormArray(index, bottomRow);
        break;
      }

      case 'remove-bottom': {
        const lastindex = this.conversions.length - 1;
        this.conversions = this.conversions.slice(0, -1);
        const formArray = this.formGroup.controls.lines as FormArray;
        formArray.removeAt(lastindex);
        break;
      }
    }
  }

  private createConversionFormControl(line: DosatorConversions): FormGroup {
    const kilogram = new FormControl(line.kilogram, [Validators.required]);
    const liter = new FormControl(line.liter, [Validators.required]);
    const kilogramPerLiter = new FormControl(line.kilogramPerLiter, [Validators.required]);

    return new FormGroup({
      liter,
      kilogram,
      kilogramPerLiter,
    });
  }

  calculateKilogramPerLiter(index: number) {
    const kilogram = this.formGroup.controls.lines.value[index]?.kilogram;
    const liter = this.formGroup.controls.lines.value[index]?.liter;

    if (kilogram > 0 && liter > 0) {
      const formArray = this.formGroup.controls.lines as FormArray;
      formArray.controls[index].patchValue({
        liter,
        kilogram,
        kilogramPerLiter: kilogram / liter,
      });
    }
  }
}
